import styles from '../Navigation.module.scss'
import NavigationItem from '../navigation-item/NavigationItem.tsx'
import { routes } from 'types/routes.ts'
import { useTranslation } from 'react-i18next'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import Link from 'components/link/Link.tsx'
import classNames from 'classnames'
import { Button, Heading } from '@boltenergy-be/design-system'
import LanguageSwitchButton from 'components/language-switch-button/LanguageSwitchButton.tsx'
import { checkHasBudgetMeter, isTerminatedContractPastEndDate } from 'utils/contracts.ts'
import useGetAccessRight from 'hooks/useSelectAccessRight.ts'
import { logout } from 'utils/app.ts'
import { setUserType } from 'utils/userType.ts'
import { getUserTypeLoginPage } from 'utils/user.ts'
import { useNavigate } from 'react-router'
import useWindowSize from 'hooks/useWindowSize.tsx'
import { SMALL_DESKTOP_BREAKPOINT } from 'constants/viewport.ts'
import { useEffect } from 'react'
import { useGetActiveReferralActionQuery } from 'store/queries/cms-api'
import { LANGUAGES } from '@boltenergy-be/shared'
import { selectIsProducerAccount } from 'store/contact/slice.ts'
import { trackEvent } from 'utils/tracking.ts'
import { EVENT_CATEGORIES } from 'constants/tracking.ts'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'
import { UserTypes } from 'store/auth/types.ts'

const MoreNavigation = () => {
  // Redux
  const { userType } = useStoreSelector((store) => store.auth)
  const { selected, accounts, billingContracts } = useStoreSelector((store) => store.contact)
  const isProducer = useStoreSelector(selectIsProducerAccount)

  // Not using the Redux selector because this is also used for Sales Portal
  const electricity =
    userType !== UserTypes.SALES && selected.billingContract && billingContracts
      ? billingContracts[selected.billingContract]?.serviceContracts?.electricity
      : undefined

  // i18n
  const { t } = useTranslation()
  const language = useGetLanguage()

  // Referral action
  const { data: activeAction } = useGetActiveReferralActionQuery({ language }, { skip: true }) // Skipped because there is no active referral action

  // Window size
  const { isSmaller } = useWindowSize(SMALL_DESKTOP_BREAKPOINT)

  // Router
  const navigate = useNavigate()

  // Access rights
  const billingCyclesAccessRights = useGetAccessRight(ACCESS_RIGHT_KEYS.billingCycles)
  const marketPricesAccessRights = useGetAccessRight(ACCESS_RIGHT_KEYS.marketPrices)

  // Constants
  const terminatedContractPastEndDate = isTerminatedContractPastEndDate(electricity)
  const hasBudgetMeter = checkHasBudgetMeter(electricity)

  /**
   * Handles the logout with correct redirect
   */
  const handleLogout = () =>
    logout(() => {
      setUserType(null)
      void navigate(getUserTypeLoginPage(userType))
    })

  // Redirect to overview if not desktop
  useEffect(() => {
    if (!isSmaller) {
      void navigate(routes.OVERVIEW, { replace: true })
    }
  }, [isSmaller, navigate])

  return (
    <nav className={classNames(styles.navigation, styles['more-navigation'])} aria-labelledby="main-more">
      <header className={styles.header}>
        <Heading as="h1" variant="h3">
          {t(($) => $.nav.more, {
            ns: 'common'
          })}
        </Heading>
        <LanguageSwitchButton />
      </header>

      {accounts?.[selected.account] && (
        <div className={styles['account-info-card']}>
          <strong>{accounts[selected.account].name}</strong>
          <small className="text-subdued">
            {accounts[selected.account].billingContracts.length === 1
              ? selected.account
              : `${accounts[selected.account].billingContracts.length} ${t(($) => $.nav.addressCard.addresses)}`}
          </small>
          {accounts && Object.keys(accounts).length > 1 && (
            <Button
              representation="link"
              onClick={() => {
                void navigate(routes.ACCOUNT_SELECTION)
              }}
            >
              {t(($) => $.nav.addressCard.switchContract)}
            </Button>
          )}
        </div>
      )}

      <ul className={styles['nav-items-list']}>
        {!terminatedContractPastEndDate && billingCyclesAccessRights.canAccess.current && !hasBudgetMeter && (
          <NavigationItem
            isMore
            icon="thumbsUp"
            text={t(($) => $.nav.instalmentCheck)}
            url={routes.BILLING_INSTALMENT_CHECK}
            onClick={() =>
              trackEvent(
                { category: EVENT_CATEGORIES.INSTALMENT_CHECK, object: 'navigation', action: 'click' },
                { navigationType: 'mobile' }
              )
            }
          />
        )}
        {marketPricesAccessRights.todayTomorrow.canAccess && (
          <NavigationItem isMore icon="euro" text={t(($) => $.nav.marketPrices)} url={routes.MARKET_PRICES} />
        )}
        {!isProducer && <NavigationItem isMore icon="home" text={t(($) => $.nav.move)} url={routes.MOVE} />}
        {!isProducer && (
          <NavigationItem
            isMore
            icon="peopleHeart"
            text={t(($) => $.nav.friends)}
            url={routes.REFERRAL_INFO}
            tag={
              activeAction
                ? {
                    text: t(($) => $.action),
                    type: 'informative'
                  }
                : undefined
            }
          />
        )}
        <NavigationItem
          isMore
          icon="electricity"
          text={t(($) => $.nav[isProducer ? 'myProducerProfile' : 'myProducer'])}
          url={routes.PRODUCER}
        />
        {isProducer && (
          <NavigationItem isMore icon="badgeCheck" text={t(($) => $.nav.guaranteeOfOrigin)} url={routes.GUARANTEE_OF_ORIGIN} />
        )}
        {!isProducer && <NavigationItem isMore icon="leaf" text={t(($) => $.nav.energyOverview)} url={routes.ENERGY_OVERVIEW} />}
        <NavigationItem isMore icon="contract" text={t(($) => $.nav.myAddresses)} url={routes.CONTRACT} />
        <NavigationItem isMore icon="userCircle" text={t(($) => $.nav.user)} url={routes.USER_PERSONAL} />
        <NavigationItem isMore icon="speechQuestion" text={t(($) => $.nav.support)} url={routes.SUPPORT_CONTACT} />
        <NavigationItem isMore icon="logOut" text={t(($) => $.nav.logout)} onClick={handleLogout} id="logout" />
      </ul>

      <ul className={styles['legal-list']}>
        <li>
          <Link
            size="small"
            variant="tertiary"
            external
            hideExternalIcon
            href={`https://www.boltenergie.be/${language === LANGUAGES.FR ? 'fr/termes-et-conditions' : 'nl/algemene-voorwaarden'}`}
          >
            {t(($) => $.footer.terms)}
          </Link>
        </li>
        <li>
          <Link
            size="small"
            variant="tertiary"
            external
            hideExternalIcon
            href={`https://www.boltenergie.be/${
              language === LANGUAGES.FR ? 'fr/mentions-legales-politique-de-confidentialite' : 'nl/disclaimer-privacy-policy'
            }`}
          >
            {t(($) => $.footer.disclaimer)}
          </Link>
        </li>
      </ul>
    </nav>
  )
}

export default MoreNavigation
